import type { Tag } from '../content/tags';
import { armorReduction } from '../content/stats';
import type { World } from './world';
import type { AnyUnit, MobEnt, PlayerEnt } from './types';
import { armorAdd, dmgDealtMul, dmgTakenMul, stacksOf, applyStatus, removeStatus } from './status';
import { fireHooks } from './actions';
import { onMobKilled, defOf } from './mobs';
import { dist } from '../math';

export interface DamageOpts {
  crit?: boolean;
  fromSkill?: string;
  noThorns?: boolean;
  isDot?: boolean;
  noHooks?: boolean;
  /** damage already moved from a bonded master onto its summon */
  redirected?: boolean;
}

/** Applies damage from source to target. Returns damage actually dealt to hp+shield. */
export function dealDamage(world: World, source: AnyUnit | null, target: AnyUnit, raw: number, tags: Tag[], opts: DamageOpts = {}): number {
  if (target.dead || raw <= 0 || target.untargetable) return 0;
  if (target.kind === 'player') {
    const p = target as PlayerEnt;
    if (world.tick < p.invulnUntil && !opts.isDot) return 0;
    if (world.duel && world.duel.phase !== 'fight') return 0;
  }
  // bonded summons take the master's hits: 90% into the bond, guards soak a share while they hold the ring
  if (target.kind === 'player' && !opts.redirected && !opts.isDot) {
    const p = target as PlayerEnt;
    if (p.flags.has('bondRedirect')) {
      const bond = summonsOf(world, p).find((m) => !!defOf(m.def).bond);
      if (bond) {
        dealDamage(world, source, bond, raw * 0.9, tags, { ...opts, redirected: true, noThorns: true, noHooks: true });
        raw *= 0.1;
      }
    }
    const guards = summonsOf(world, p).filter((m) => !!defOf(m.def).guard && dist(m.x, m.y, p.x, p.y) < 150);
    if (guards.length) {
      const soak = Math.min(0.6, 0.2 * guards.length);
      const g = guards[world.tick % guards.length];
      dealDamage(world, source, g, raw * soak, tags, { ...opts, redirected: true, noThorns: true, noHooks: true });
      raw *= 1 - soak;
    }
    if (raw <= 0) return 0;
  }
  let dmg = raw;
  if (source) {
    dmg *= dmgDealtMul(source);
    if (source.kind === 'player') {
      const sp = source as PlayerEnt;
      if (sp.flags.has('lowHpDamage')) dmg *= 1 + (1 - sp.hp / sp.maxHp) * 0.9;
    }
  }
  dmg *= dmgTakenMul(target);
  const armor = target.stats.armor + armorAdd(target);
  dmg *= 1 - armorReduction(armor);
  dmg = Math.max(1, Math.round(dmg));

  let toHp = dmg;
  if (target.shield > 0) {
    const absorbed = Math.min(target.shield, toHp);
    target.shield -= absorbed;
    toHp -= absorbed;
    if (target.shield <= 0) removeStatus(world, target, 'shield');
  }
  if (target.kind !== 'player') {
    const m = target as MobEnt;
    if (m.affixShield > 0) {
      const absorbed = Math.min(m.affixShield, toHp);
      m.affixShield -= absorbed;
      toHp -= absorbed;
    }
  }
  target.hp -= toHp;
  target.lastHurt = world.tick;
  if (source) target.lastHitBy = source.id;
  world.events.push({ t: 'hit', id: target.id, x: target.x, y: target.y, dmg, crit: !!opts.crit, tag: tags[0], team: source?.team ?? -1 });

  if (source && !opts.isDot) {
    if (source.stats.lifesteal > 0) heal(world, source, dmg * source.stats.lifesteal, source);
    if (source.kind === 'player') {
      const sp = source as PlayerEnt;
      sp.damageDealt += dmg;
      if (!opts.noHooks) {
        fireHooks(world, sp, 'hit', { target, tags, x: target.x, y: target.y });
        if (opts.crit) fireHooks(world, sp, 'crit', { target, tags, x: target.x, y: target.y });
      }
    } else if ((source as MobEnt).kind === 'summon') {
      const owner = world.players.get((source as MobEnt).owner);
      if (owner && owner.stats.lifesteal > 0) heal(world, owner, dmg * owner.stats.lifesteal * 0.5, owner);
    }
    // thorns reflect direct hits
    const thorns = target.stats.thorns + (target.kind === 'player' && (target as PlayerEnt).flags.has('thornsScaleArmor') ? target.stats.armor / 100 : 0);
    if (thorns > 0 && !opts.noThorns && source.team !== target.team) {
      dealDamage(world, target, source, dmg * thorns, ['blunt'], { noThorns: true, noHooks: true });
    }
  }
  if (target.kind === 'player') {
    const tp = target as PlayerEnt;
    tp.damageTaken += dmg;
    if (!opts.noHooks) {
      fireHooks(world, tp, 'damaged', { target: source ?? undefined, tags, x: tp.x, y: tp.y });
      if (tp.hp / tp.maxHp < 0.4 && tp.lowHpArmed) {
        tp.lowHpArmed = false;
        fireHooks(world, tp, 'lowhp', { x: tp.x, y: tp.y });
      }
    }
  }
  if (target.hp <= 0) kill(world, target, source);
  return dmg;
}

function summonsOf(world: World, p: PlayerEnt): MobEnt[] {
  const out: MobEnt[] = [];
  for (const id of p.summons) {
    const m = world.mobs.get(id);
    if (m && !m.dead) out.push(m);
  }
  return out;
}

export function heal(world: World, target: AnyUnit, amount: number, source: AnyUnit | null): number {
  if (target.dead || amount <= 0) return 0;
  if (target.kind === 'player' && (target as PlayerEnt).flags.has('noHeal')) return 0;
  if (source && source.kind === 'player') amount *= source.stats.healPower;
  const a = Math.min(target.maxHp - target.hp, Math.round(amount));
  if (a <= 0) return 0;
  target.hp += a;
  if (target.kind === 'player' && target.hp / target.maxHp >= 0.6) (target as PlayerEnt).lowHpArmed = true;
  world.events.push({ t: 'hit', id: target.id, x: target.x, y: target.y, dmg: a, crit: false, heal: true, team: target.team });
  return a;
}

export function kill(world: World, victim: AnyUnit, killer: AnyUnit | null): void {
  if (victim.dead) return;
  victim.dead = true;
  victim.hp = 0;
  const killerName = killer ? displayName(world, killer) : undefined;
  if (victim.kind === 'player') {
    const p = victim as PlayerEnt;
    p.deathCause = killer ? { name: killerName ?? '', kind: killer.kind === 'player' ? 'player' : (killer as MobEnt).def } : { name: '', kind: 'unknown' };
    world.events.push({ t: 'death', id: p.id, kind: 'player', x: p.x, y: p.y, name: p.name, killer: killerName });
    // summons die with their master
    for (const sid of p.summons) {
      const s = world.mobs.get(sid);
      if (s && !s.dead) {
        s.dead = true;
        world.events.push({ t: 'death', id: s.id, kind: 'summon', x: s.x, y: s.y, def: s.def });
      }
    }
    p.summons = [];
    if (p.pet) {
      const pet = world.mobs.get(p.pet.entityId);
      if (pet) {
        pet.dead = true;
        world.events.push({ t: 'death', id: pet.id, kind: 'summon', x: pet.x, y: pet.y, def: pet.def });
      }
    }
    p.respawnAt = world.tick + world.seconds(2);
    world.listeners.onPlayerDeath?.(p, killer);
    if (killer && killer.kind === 'player') {
      world.events.push({ t: 'feed', key: 'feed.pvpKill', vars: { killer: killerName ?? '', victim: p.name } });
    }
    return;
  }
  const m = victim as MobEnt;
  world.events.push({ t: 'death', id: m.id, kind: m.kind, x: m.x, y: m.y, def: m.def, name: m.kind === 'revenant' ? m.name : undefined, killer: killerName });
  onMobKilled(world, m, killer);
}

export function displayName(world: World, u: AnyUnit): string {
  if (u.kind === 'player') return (u as PlayerEnt).name;
  const m = u as MobEnt;
  if (m.kind === 'revenant') return m.name;
  if (m.kind === 'summon') {
    const owner = world.players.get(m.owner);
    return owner ? owner.name : m.def;
  }
  return m.def;
}

/** Rolls a crit for a source and returns the multiplier. */
export function rollCrit(world: World, source: AnyUnit | null): { crit: boolean; mult: number } {
  if (!source) return { crit: false, mult: 1 };
  if (world.rng.chance(source.stats.critChance)) return { crit: true, mult: source.stats.critMult };
  return { crit: false, mult: 1 };
}

export { applyStatus, stacksOf };
